import type {Permissions} from '@nursio-link/common/authorization';
import {isAuthorized} from '@nursio-link/common/authorization';
import {useMutation, useQuery} from '@tanstack/react-query';

import type {AuthUser} from '@/services/authService';
import {
  getCurrentUserQueryOptions,
  getGroupsQueryOptions,
  signOutMutationOptions,
} from '@/services/authService';
import type {UserGroup} from '@/services/graphqlHooks';
import isGroupAuthorized from '@/utils/isGroupAuthorized';

/***********************
 * Auth Query Hooks
 ***********************/

export const useAuthUser = (user?: AuthUser | null) => {
  return useQuery({...getCurrentUserQueryOptions, initialData: user});
};

export const useGroups = () => {
  const {data: groups} = useQuery(getGroupsQueryOptions);
  if (!groups) {
    return;
  }

  return groups;
};

/***********************
 * Auth Mutation Hooks
 ***********************/

export const useSignOut = () => {
  const signOut = useMutation({
    ...signOutMutationOptions,
    onSuccess: () => {
      window.location.href = '/login';
    },
  });
  return signOut;
};

/***********************
 * Auth Helpers Hooks
 ***********************/

export const useGroupAuthorize = (allowedGroups: UserGroup[]) => {
  const currentGroups = useGroups();

  if (!currentGroups) return false;
  return isGroupAuthorized(currentGroups, allowedGroups);
};

export const useAuthorize = (permission: Permissions) => {
  const currentGroups = useGroups();

  if (!currentGroups) return false;
  return isAuthorized(currentGroups, permission);
};
